/**
 * ДЕКОР ДОЕЗЖАЕТ ДО РЕАЛТАЙМА — ИЛИ ОТКАЗЫВАЕТ ВСЛУХ.
 *
 * Паспорт материала приходит в сцену ЦЕЛИКОМ: `holzcom.object_material_passport.v2`
 * несёт `materialId` и `article` (например `EGGER/W1000-ST9-16`). Реалтайм-ветка
 * (`objectSurfaceContract`) читала из него только `profile` и `colorHex` и
 * ОТБРАСЫВАЛА артикул, а таблица сканов `DECOR_TEXTURE_BY_CODE` имела ровно
 * одного потребителя — офлайновый путь трассировки. То есть таблица была
 * сиротой: её данные не доходили до того, что человек видит на экране.
 *
 * ЗАМЕР, КОТОРЫЙ МЕНЯЕТ СМЫСЛ ЗАДАЧИ. На живой модели
 * `evidence/kostya-commercial-9119c1b8` — 159 плит с паспортом и ТРИ артикула:
 * 109 `EGGER/W1000-ST9-16`, 32 `HC.FACADE.PAINT.M`, 18 `HDF/04-WHITE`.
 * Ни один из них не покрыт сканом: в таблице пять ключей
 * (H1180, H1277, H3303, U999, W1100), и `W1000` среди них НЕТ. Довести артикул
 * до реалтайма — необходимо, но на этой модели это НЕ красит ни одной панели.
 * Белизна здесь честная: «Белый Премиум», «Эмаль матовая» и «ХДФ белая» —
 * это и есть заказанные материалы.
 *
 * ПОЭТОМУ ГЛАВНОЕ ЗДЕСЬ — НЕ ПОДСТАНОВКА, А НАЗВАННЫЙ ОТКАЗ. Молчаливая белая
 * плита неотличима от плиты, у которой декор потерялся. Panel, у которой
 * артикул несёт код декора, а скана нет, обязана назвать себя кодом
 * `SCENE_DECOR_SCAN_MISSING`, а не притворяться белой.
 *
 * РАЗБОР — СТРОГИЙ, ПО АРТИКУЛУ, А НЕ ПО ИМЕНИ. Имя материала разбирать нельзя:
 * на этом дереве наивный разбор уже дважды ошибался («Феникс» оказался
 * ЛДСП-декором, `U998 ST38 Лес чёрный` — однотоном). Артикул структурен
 * (`ПОСТАВЩИК/КОД-ОТДЕЛКА-ТОЛЩИНА`), и код берётся из него позиционно.
 * Соответствие обязано быть измеримым: либо ключ совпал, либо отказ.
 */
import { DECOR_TEXTURE_BY_CODE } from './pt-presets';

/** Screen-born refusal code; its words live in `PRO100_COMMAND_HINTS`. */
export const DECOR_SCAN_MISSING_CODE = 'SCENE_DECOR_SCAN_MISSING';

export type DecorScanDecision =
  /** A decor code was read from the passport and a real scan ships for it. */
  | { kind: 'scan'; code: string; url: string }
  /** A decor code was read, but no scan ships — a named refusal, never silent white. */
  | { kind: 'missing'; code: string; refusalCode: typeof DECOR_SCAN_MISSING_CODE }
  /** The article carries no decor code at all (paint, HDF, hardware): not a decor. */
  | { kind: 'unkeyed' };

const DECOR_CODE = /^[HWUF]\d{3,4}$/;

/**
 * Read the decor code out of a structured catalogue article, positionally.
 *
 * `EGGER/W1000-ST9-16` → `W1000`.  `HC.FACADE.PAINT.M` and `HDF/04-WHITE`
 * carry no decor code and yield null — that is an answer, not a failure.
 * No name is parsed here on purpose: a display label must never decide
 * material identity.
 */
export function decorCodeFromArticle(article: string | null | undefined): string | null {
  const text = String(article ?? '').trim().toUpperCase();
  const slash = text.indexOf('/');
  if (slash < 0) return null;
  const segment = text.slice(slash + 1).split('-')[0]?.trim() ?? '';
  return DECOR_CODE.test(segment) ? segment : null;
}

/**
 * Decide what surface a panel's material passport is entitled to.
 *
 * Returns one of three NAMED outcomes so a caller can render a real scan,
 * report a missing decor by code, or record that the material is not a decor
 * at all.  There is no fourth, silent branch.
 */
/**
 * RUNTIME-РЕЕСТР СКАНОВ (волна 2): дистрибьюторские пакеты, загруженные через
 * POST /api/materials/decor-scans, имеют приоритет над зашитой таблицей —
 * иначе новый скан потребовал бы релиза фронта. Гидратация один раз на старте.
 *
 * АУДИТ ПРОДА 10.10 (P2): флаг `hydrated` взводился ДО fetch и никогда не
 * сбрасывался. Первая неудача (блып сервера / 500 / 401-гонка с логином)
 * запирала реестр в «пустом» состоянии до перезагрузки страницы, и релогин не
 * лечил его — AuthGate.markAuthed вызывал гидратацию, которая молча выходила
 * по флагу. Теперь флаг взводится ТОЛЬКО после успешного ответа: неудача
 * оставляет окно открытым, и следующий markAuthed (после релогина) повторяет
 * попытку. Шторм ограничен счётчиком: не более MAX_HYDRATION_ATTEMPTS сетевых
 * попыток за сессию — цикл revoke/релогин не превращается в дождь из 401.
 *
 * Пользовательская смена сессии (logout/revoke) обязана чистить реестр — иначе
 * юзер B наследует сканы юзера A: см. `resetDecorScans`, зовущийся из
 * `clearAuthenticatedCache` (единая идемпотентная точка чистки кэшей в
 * auth-пути).
 */
const runtimeScans = new Map<string, string>();
let hydrated = false;
let hydrationAttempts = 0;
let hydrationInFlight: Promise<void> | null = null;

/** Максимум сетевых попыток гидратации за сессию страницы. */
const MAX_HYDRATION_ATTEMPTS = 3;

export async function hydrateDecorScans(): Promise<void> {
  if (hydrated) return;
  if (hydrationAttempts >= MAX_HYDRATION_ATTEMPTS) return;
  if (hydrationInFlight) return hydrationInFlight;
  hydrationAttempts += 1;
  hydrationInFlight = (async () => {
    try {
      const r = await fetch('/api/materials/decor-scans');
      if (!r.ok) return; // hydrated остаётся false — релогин повторит попытку
      const data = await r.json() as { entries?: Array<{ code: string; url: string }> };
      for (const entry of data.entries ?? []) {
        if (entry?.code && entry?.url) runtimeScans.set(entry.code, entry.url);
      }
      hydrated = true;
    } catch {
      /* реестр недоступен — работаем на зашитой таблице; попытка повторится */
    } finally {
      hydrationInFlight = null;
    }
  })();
  return hydrationInFlight;
}

/**
 * Полный сброс runtime-реестра: сессия кончилась (logout / 401-revoke), и
 * сканы предыдущего пользователя не имеют права переживать её. Чистит и
 * флаг гидратации, и накопленные записи; счётчик попыток НЕ трогает — шторм-
 * ограничение действует на всю сессию страницы. Идемпотентен.
 */
export function resetDecorScans(): void {
  hydrated = false;
  hydrationInFlight = null;
  runtimeScans.clear();
}

export function resolveDecorScan(article: string | null | undefined): DecorScanDecision {
  const code = decorCodeFromArticle(article);
  if (!code) return { kind: 'unkeyed' };
  const url = runtimeScans.get(code) ?? DECOR_TEXTURE_BY_CODE[code];
  if (url) return { kind: 'scan', code, url };
  return { kind: 'missing', code, refusalCode: DECOR_SCAN_MISSING_CODE };
}

/**
 * The cache discriminator for one decor decision.
 *
 * The realtime material cache keyed on `profile|colorHex|glassy|repeat` only.
 * Two different decors that happen to share a base colour would have collided
 * on one cached material, so the decor has to be part of the key.
 */
export function decorCacheKey(decision: DecorScanDecision): string {
  if (decision.kind === 'scan') return `scan:${decision.code}`;
  if (decision.kind === 'missing') return `missing:${decision.code}`;
  return 'unkeyed';
}

export type DecorRefusal = Readonly<{ entityId: string; code: string; article: string }>;

/**
 * Panels whose decor was named and refused, keyed by entity.
 *
 * The registry exists so the refusal is READABLE — by a check, by an
 * inspector, by a person looking at the stand — instead of dying as a colour
 * nobody can tell from an intended white.  Keyed by entity id so a rebuild of
 * the same scene reports each panel once.
 */
const refusalsByEntity = new Map<string, DecorRefusal>();

declare global {
  interface Window { __HOLZCOM_DECOR_REFUSALS?: DecorRefusal[] }
}

export function recordDecorRefusal(entityId: string, code: string, article: string): void {
  refusalsByEntity.set(String(entityId), { entityId: String(entityId), code, article });
  if (typeof window !== 'undefined') window.__HOLZCOM_DECOR_REFUSALS = listDecorRefusals();
}

export function listDecorRefusals(): DecorRefusal[] {
  return [...refusalsByEntity.values()];
}

export function resetDecorRefusals(): void {
  refusalsByEntity.clear();
  if (typeof window !== 'undefined') window.__HOLZCOM_DECOR_REFUSALS = [];
}
